// The SOW detail page's "copy milestone table": an HTML table for pasting into
// an email and a tab-separated copy for pasting into a spreadsheet.
//
// Pen test AD26341, finding AD11, retest. Both went onto the clipboard raw. A
// milestone description of "=HYPERLINK(...)" ran as a formula when pasted into
// Excel, and one holding markup became markup in the email. So the plain copy
// goes through the same formula guard as every CSV export (utils/csv.js), with
// tabs and line breaks inside a cell flattened so it cannot spill into the next
// column or row, and every value in the HTML copy is escaped.
//
// The amounts and dates are formatted here from numbers and dates, so they are
// left as they are: a negative amount stays a number in the spreadsheet.
import { tsvCell, escapeHtml } from './csv.js';

const HEADERS = ['Milestone', 'Description', 'Status', 'Contract', 'Forecast', 'Amount'];

/**
 * { html, plain } for a milestone table. `rows` are
 * { code, desc, status, contract, forecast, amount } with dates and amounts
 * already formatted; `total` is the formatted total. `showAmount` false leaves
 * the Amount column and the total out, for somebody without sow:see-financials.
 */
export function milestoneTableClipboard({ title, rows, total, showAmount = true }) {
  const headers = showAmount ? HEADERS : HEADERS.slice(0, -1);
  const thStyle = 'padding:6px 10px;text-align:left;font-size:11px;background:#f3f4f6;border-bottom:2px solid #e5e7eb;white-space:nowrap;font-weight:700;color:#374151;';
  const tdStyle = 'padding:5px 10px;font-size:11px;border-bottom:1px solid #f3f4f6;vertical-align:top;';
  const h = escapeHtml;
  const html = `
<table style="border-collapse:collapse;font-family:Arial,sans-serif;width:100%;">
  <caption style="text-align:left;font-size:13px;font-weight:700;padding-bottom:6px;">${h(title)}</caption>
  <thead><tr>
    ${headers.map((label, i) => `<th style="${thStyle}${showAmount && i === headers.length - 1 ? ';text-align:right;' : ''}">${label}</th>`).join('\n    ')}
  </tr></thead>
  <tbody>
    ${rows.map((r, i) => `<tr style="background:${i % 2 === 0 ? '#ffffff' : '#f9fafb'};">
      <td style="${tdStyle}font-weight:700;white-space:nowrap;">${h(r.code)}</td>
      <td style="${tdStyle}">${h(r.desc)}</td>
      <td style="${tdStyle}white-space:nowrap;">${h(r.status)}</td>
      <td style="${tdStyle}white-space:nowrap;">${h(r.contract)}</td>
      <td style="${tdStyle}white-space:nowrap;">${h(r.forecast)}</td>
      ${showAmount ? `<td style="${tdStyle}text-align:right;white-space:nowrap;">${h(r.amount)}</td>` : ''}
    </tr>`).join('')}
    ${showAmount ? `<tr style="background:#f3f4f6;">
      <td colspan="5" style="${tdStyle}text-align:right;font-weight:700;">Total</td>
      <td style="${tdStyle}text-align:right;font-weight:700;white-space:nowrap;">${h(total)}</td>
    </tr>` : ''}
  </tbody>
</table>`;

  const flat = (v) => String(v ?? '').replace(/[\t\r\n]+/g, ' ');
  const plain = [
    tsvCell(title),
    '',
    headers.join('\t'),
    ...rows.map(r => [tsvCell(r.code), tsvCell(r.desc), tsvCell(r.status), tsvCell(r.contract), tsvCell(r.forecast), ...(showAmount ? [flat(r.amount)] : [])].join('\t')),
    ...(showAmount ? [['', '', '', '', 'Total', flat(total)].join('\t')] : []),
  ].join('\n');

  return { html, plain };
}
